/**
 * Reviews floating layer (Ph6) — pure decision core.
 *
 * The brain of the FOMO popup, with ZERO React/DOM/storage access so every
 * branch is unit-testable. `ReviewsFloatingHost` owns the side effects (timers,
 * localStorage reads/writes, DOM) and calls these pure helpers to decide.
 *
 * Mirrors the proven frequency-cap shape of shared-ui's PopupHost, but the
 * reviews layer is data-driven (rotates live cached_reviews) rather than
 * showing one authored popup — hence the rotation index helper.
 */

import type { ReviewCardData } from '@sifly/shared-ui/content'

// ── Storage keys ────────────────────────────────────────────────
// FOMO popup state. last-shown + count are persistent (localStorage); the
// rage-close backoff is per-session (sessionStorage) so a frustrated visitor
// gets peace for this visit but the popup can return next time.
export const FOMO_LAST_SHOWN_KEY = 'sifly-reviews-fomo-last-shown'
export const FOMO_COUNT_KEY = 'sifly-reviews-fomo-count'
export const FOMO_INDEX_KEY = 'sifly-reviews-fomo-index'
export const FOMO_BACKOFF_KEY = 'sifly-reviews-fomo-backoff'

// ── Calm-test defaults ──────────────────────────────────────────
export const FOMO_COOLDOWN_DAYS = 1
export const FOMO_MAX_IMPRESSIONS = 3
export const RAGE_CLOSE_MS = 2000

export type FomoEligibilityInput = {
  /** Current time (ms epoch). */
  now: number
  /** Last-shown timestamp (ms epoch), or null if never shown. */
  lastShown: number | null
  /** Lifetime impressions for this visitor. */
  count: number
  /** Session rage-close backoff flag. */
  backoff: boolean
  cooldownDays?: number
  maxImpressions?: number
}

/**
 * Is the FOMO popup allowed to show right now? Calm gate: never while backed
 * off this session, never past the lifetime cap, never inside the cooldown.
 */
export function isFomoEligible(input: FomoEligibilityInput): boolean {
  const { now, lastShown, count, backoff } = input
  const cooldownDays = input.cooldownDays ?? FOMO_COOLDOWN_DAYS
  const maxImpressions = input.maxImpressions ?? FOMO_MAX_IMPRESSIONS
  if (backoff) return false
  if (count >= maxImpressions) return false
  if (lastShown !== null) {
    const elapsed = now - lastShown
    const cooldownMs = cooldownDays * 24 * 60 * 60 * 1000
    if (elapsed < cooldownMs) return false
  }
  return true
}

/**
 * Round-robin pick across page-views so repeat visitors see review variety —
 * one at a time. prevIndex null/garbage starts the rotation at 0.
 */
export function nextRotationIndex(prevIndex: number | null, poolLength: number): number {
  if (poolLength <= 0) return 0
  const prev = prevIndex == null || !Number.isFinite(prevIndex) ? -1 : prevIndex
  return (((prev + 1) % poolLength) + poolLength) % poolLength
}

/**
 * A dismiss faster than the threshold reads as annoyance — back off for the
 * rest of the session.
 */
export function isRageClose(
  shownAtMs: number,
  dismissedAtMs: number,
  thresholdMs: number = RAGE_CLOSE_MS,
): boolean {
  return dismissedAtMs - shownAtMs < thresholdMs
}

/** Arbitration: never toast over an open hub panel. */
export function shouldSuppressPopup(input: { hubOpen: boolean }): boolean {
  return input.hubOpen
}

/** Parse an int out of a possibly-null storage string; null when absent/NaN. */
export function parseStoredInt(raw: string | null): number | null {
  if (raw == null) return null
  const n = parseInt(raw, 10)
  return Number.isFinite(n) ? n : null
}

// ── Teaser sequence (staged intro) ──────────────────────────────
// The redesign replaces the single FOMO toast with a short staged sequence:
// an aggregate card, then one real review, then nothing but the indicator.
// Timings are named so the calm budget can be asserted on in tests.
export const TEASER_DELAY_MS = 6000
export const TEASER_FADE_MS = 350
export const TEASER_HOLD_AGGREGATE_MS = 4500
export const TEASER_HOLD_REVIEW_MS = 5500
export const TEASER_GAP_MS = 250
/** Calm ceiling — the whole sequence must finish inside this. */
export const TEASER_MAX_TOTAL_MS = 20000

export type TeaserItem =
  | { kind: 'aggregate'; companyName: string; average: number; count: number }
  | { kind: 'review'; review: ReviewCardData }

/** Where the sequence is right now. `index` addresses the sequence array;
 *  `gap` is the dead air AFTER `index`, before `index + 1`. */
export type TeaserPhase =
  | { stage: 'idle' }
  | { stage: 'in'; index: number }
  | { stage: 'hold'; index: number }
  | { stage: 'out'; index: number }
  | { stage: 'gap'; index: number }
  | { stage: 'done' }

export const TEASER_START: TeaserPhase = { stage: 'idle' }

/**
 * The cards to show, in order: the aggregate (which names the dealer and
 * establishes scale) then one real review. No aggregate → nothing at all,
 * so a tenant with zero reviews never renders "rated 0.0 from 0 reviews".
 */
export function buildTeaserSequence(input: {
  companyName: string
  average: number
  count: number
  review: ReviewCardData | null
}): TeaserItem[] {
  const { companyName, average, count, review } = input
  if (count <= 0) return []
  const items: TeaserItem[] = [{ kind: 'aggregate', companyName, average, count }]
  if (review) items.push({ kind: 'review', review })
  return items
}

/** Advance one step. Always terminates at `done` — never cycles. */
export function nextTeaserPhase(phase: TeaserPhase, length: number): TeaserPhase {
  switch (phase.stage) {
    case 'idle':
      return length > 0 ? { stage: 'in', index: 0 } : { stage: 'done' }
    case 'in':
      return { stage: 'hold', index: phase.index }
    case 'hold':
      return { stage: 'out', index: phase.index }
    case 'out':
      return phase.index + 1 < length ? { stage: 'gap', index: phase.index } : { stage: 'done' }
    case 'gap':
      return { stage: 'in', index: phase.index + 1 }
    default:
      return { stage: 'done' }
  }
}

/** How long the host should wait before advancing out of `phase`. */
export function teaserPhaseDurationMs(phase: TeaserPhase, items: TeaserItem[]): number {
  switch (phase.stage) {
    case 'idle':
      return TEASER_DELAY_MS
    case 'in':
    case 'out':
      return TEASER_FADE_MS
    case 'hold':
      return items[phase.index]?.kind === 'review' ? TEASER_HOLD_REVIEW_MS : TEASER_HOLD_AGGREGATE_MS
    case 'gap':
      return TEASER_GAP_MS
    default:
      return 0
  }
}

/** Total wall-clock of the whole sequence, for the calm-budget assertion. */
export function teaserSequenceDurationMs(items: TeaserItem[]): number {
  if (items.length === 0) return 0
  let phase = TEASER_START
  let total = 0
  // Bounded walk. nextTeaserPhase always reaches 'done'; the cap is a runaway
  // guard so a future edit that introduces a cycle fails loudly here first.
  for (let i = 0; i < 64 && phase.stage !== 'done'; i++) {
    total += teaserPhaseDurationMs(phase, items)
    phase = nextTeaserPhase(phase, items.length)
  }
  return total
}

/** Which card is mounted, or null. Mounted across `out` too — a card has to
 *  still be in the DOM to animate away. */
export function teaserVisibleIndex(phase: TeaserPhase): number | null {
  return phase.stage === 'in' || phase.stage === 'hold' || phase.stage === 'out' ? phase.index : null
}

/** True only while the mounted card is fading out. */
export function isTeaserFadingOut(phase: TeaserPhase): boolean {
  return phase.stage === 'out'
}
